import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const navigationItems = [
    { name: 'Inicio', path: '/' },
    { name: 'Servicios', path: '/servicios' },
    { name: 'Cotización', path: '/cotizacion' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contacto', path: '/contacto' }
  ];

  const logoVariants = {
    hidden: { 
      scale: 0,
      rotate: -45,
      opacity: 0
    },
    visible: { 
      scale: 1,
      rotate: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 180,
        damping: 25,
        duration: 1.5,
        delay: 0.3
      }
    },
    hover: {
      scale: 1.08,
      filter: "brightness(1.1) drop-shadow(0 8px 25px rgba(34, 197, 94, 0.25))",
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    },
    tap: {
      scale: 0.95,
      transition: {
        duration: 0.1
      }
    }
  };

  const glowVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: [0, 0.4, 0],
      scale: [1, 1.15, 1],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatDelay: 6,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.header 
      className="bg-white shadow-lg sticky top-0 z-50"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 sm:py-5">
          <Link to="/" className="flex-shrink-0 relative">
            <motion.div
              className="absolute inset-0 rounded-full bg-green-500/8 blur-2xl"
              variants={glowVariants}
              initial="hidden"
              animate="visible"
            />
            <motion.img 
              src="https://utfs.io/f/5BN0V4mlt4NUZftO5IVUTK6QzFRPgbcvS34JeBN2tCIwlfjU"
              alt="REZCOM INTERNATIONAL - Logística Global" 
              className="h-14 sm:h-16 lg:h-20 w-auto relative z-10"
              variants={logoVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              whileTap="tap"
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navigationItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15 + 1.0 }}
              >
                <Link
                  to={item.path}
                  className="text-gray-700 hover:text-green-700 font-semibold transition-colors duration-300 relative group text-sm xl:text-base"
                >
                  {item.name}
                  <motion.span 
                    className="absolute -bottom-1 left-0 h-0.5 bg-green-600"
                    initial={{ width: 0 }}
                    whileHover={{ width: "100%" }}
                    transition={{ duration: 0.3 }}
                  />
                </Link>
              </motion.div>
            ))}
          </nav>

          <div className="hidden xl:flex items-center space-x-5">
            <motion.div 
              className="flex items-center space-x-4 text-sm text-gray-600"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
            >
              <motion.div 
                className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg"
                whileHover={{ 
                  scale: 1.05, 
                  backgroundColor: "#f0fdf4",
                  color: "#059669" 
                }}
                transition={{ duration: 0.2 }}
              >
                <Phone className="w-4 h-4 text-green-600" />
                <span className="hidden 2xl:inline font-medium">+52 556 215 2214</span>
                <span className="xl:inline 2xl:hidden font-medium">+52 556...</span>
              </motion.div>
              <motion.div 
                className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg"
                whileHover={{ 
                  scale: 1.05, 
                  backgroundColor: "#f0fdf4",
                  color: "#059669" 
                }}
                transition={{ duration: 0.2 }}
              >
                <Mail className="w-4 h-4 text-green-600" />
                <span className="hidden 2xl:inline font-medium">contacto@rezcom-intl.mx</span>
                <span className="xl:inline 2xl:hidden font-medium">contacto@...</span>
              </motion.div>
            </motion.div>

            <div className="relative">
              <motion.button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-full transition-colors duration-300 text-sm font-semibold shadow-lg"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.8, duration: 0.5 }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 10px 30px rgba(34, 197, 94, 0.3)"
                }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  animate={{ rotate: isUserMenuOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <User className="w-4 h-4" />
                </motion.div>
                <span>Mi Cuenta</span>
              </motion.button>

              <AnimatePresence>
                {isUserMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-3 w-52 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50"
                  >
                    <Link
                      to="/login"
                      className="block px-5 py-3 text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors duration-200 text-sm font-medium"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Iniciar Sesión
                    </Link>
                    <div className="border-t border-gray-100 my-1"></div>
                    <Link
                      to="/registro"
                      className="block px-5 py-3 text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors duration-200 text-sm font-medium"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Crear Cuenta
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center space-x-3 lg:hidden">
            <motion.div 
              className="hidden md:flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              <Phone className="w-4 h-4 text-green-600" />
              <span className="text-xs text-gray-700 font-medium">+52 556 215 2214</span>
            </motion.div>
            
            <motion.button
              className="lg:hidden p-2.5 rounded-xl hover:bg-green-50 transition-colors duration-300 border border-gray-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              transition={{ delay: 1.5, duration: 0.5 }}
              whileHover={{ 
                scale: 1.05,
                backgroundColor: "#f0fdf4",
                borderColor: "#10b981"
              }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{ rotate: isMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700" />
                )}
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* Línea verde decorativa con gradiente */}
        <motion.div 
          className="h-1.5 bg-gradient-to-r from-green-400 via-green-600 to-green-400 rounded-full"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ delay: 1.0, duration: 1.2, ease: "easeOut" }}
        />

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4 }}
              className="lg:hidden bg-white border-t border-gray-100 py-5"
            >
              <div className="space-y-5">
                {navigationItems.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={item.path}
                      className="block text-gray-700 hover:text-green-700 font-semibold py-2 px-2 rounded-lg hover:bg-green-50 transition-all duration-300"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                
                <motion.div 
                  className="border-t border-gray-200 pt-5 space-y-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="md:hidden bg-gray-50 p-4 rounded-xl">
                    <div className="flex items-center space-x-2 text-sm text-gray-700 mb-2">
                      <Phone className="w-4 h-4 text-green-600" />
                      <span className="font-medium">+52 556 215 2214</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <Mail className="w-4 h-4 text-green-600" />
                      <span className="font-medium">contacto@rezcom-intl.mx</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/login"
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white text-center py-3.5 rounded-xl transition-colors duration-300 font-semibold shadow-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Iniciar Sesión
                    </Link>
                    <Link
                      to="/registro"
                      className="flex-1 border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white text-center py-3.5 rounded-xl transition-colors duration-300 font-semibold"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Crear Cuenta
                    </Link>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;